import { useLoaderData } from 'react-router'
import { observer } from 'mobx-react-lite'
import { ContentError } from '@/components/ContentError'
import { ContentLoading } from '@/components/ContentLoading'
import { useDocumentTitle } from '@/hooks/document-title'
import { useActivation } from '@/hooks/view-model-hooks'
import { match } from '@/utils/pattern-match-util'
import {
  UserDetails,
  UserDetailsHeader,
  UserDetailsTabs,
} from '@/features/user-management/UserDetails'
import { MembershipsSidebarContainer } from '@/features/user-management/components/MembershipsSidebar/MembershipsSidebarContainer'
import { UserDetailsInformationContainer } from '@/features/user-management/components/UserDetailsInformation/UserDetailsInformationContainer'
import { MembershipsTabContainer } from '@/features/user-management/MembershipsTab/MembershipsTabContainer'
import { DocumentsTabContainer } from '@/features/documents/components/DocumentsTab/DocumentsTabContainer'
import { UserJobsTabContainer } from '@/features/jobs/list/components/UserJobsTab/UserJobsTabContainer'
import { AdvisorClientsTabContainer } from '@/features/client-user-management/ClientUserDetails/AdvisorClientsTabContainer'
import { AddUserToTeamDialogContainer } from '@/features/user-management/AddUserToTeamDialog/AddUserToTeamDialogContainer'
import { AddUserToWorkspaceDialogContainer } from '@/features/user-management/AddUserToWorkspaceDialog/AddUserToWorkspaceDialogContainer'
import { AddCustomerToWorkspaceDialogContainer } from '@/features/user-management/AddCustomerToWorkspaceDialog/AddCustomerToWorkspaceDialogContainer'
import { JoinWorkspaceAsAdminDialogContainer } from '@/features/user-management/JoinWorkspaceAsAdminDialog/JoinWorkspaceAsAdminDialogContainer'
import { EditUserInformationDialogContainer } from '@/features/user-management/EditUserInformationDialog/EditUserInformationDialogContainer'
import { EditProDetailsDialogContainer } from '@/features/user-management/EditProDetailsDialog/EditProDetailsDialogContainer'
import { EditUserRoleDialogContainer } from '@/features/user-management/EditUserRoleDialog/EditUserRoleDialogContainer'
import { RemoveFromWorkspaceDialogContainer } from '@/features/user-management/RemoveFromWorkspaceDialog/RemoveFromWorkspaceDialogContainer'
import { ManageUserCapacityDialogContainer } from '@/features/user-management/ManageUserCapacityDialog/ManageUserCapacityDialogContainer'
import { UserInfoItemListDialogContainer } from '@/features/user-management/UserInfoItemListDialog/UserInfoItemListDialogContainer'
import { ManageSkillsListDialogContainer } from '@/features/user-management/ManageSkillsListDialog/ManageSkillsListDialogContainer'
import { RestoreToWorkspaceDialogContainer } from '@/features/user-management/RestoreToWorkspaceDialog/RestoreToWorkspaceDialogContainer'
import { PromoteToOwnerDialogContainer } from '@/features/user-management/PromoteToOwnerDialog/PromoteToOwnerDialogContainer'
import type { UserDetailsViewLoaderData } from '@/features/user-management/user-management-route-loaders'
import { type UserDetailsViewModel } from './UserDetailsViewModel'

interface UserDetailsPageProps {
  vm: UserDetailsViewModel
}

export default observer(function UserDetailsContainer() {
  // The route loader provides the view model for the route's user.
  const data = useLoaderData<UserDetailsViewLoaderData>()
  useDocumentTitle('User Details')

  return data && <UserDetailsViewContainer vm={data.vm} />
})

const UserDetailsViewContainer = observer(function UserDetailsViewContainer({
  vm,
}: UserDetailsPageProps) {
  useActivation(vm)

  return vm.fetch.match({
    pending: () => <ContentLoading />,
    rejected: (err) => <ContentError error={err} />,
    resolved: () => <UserDetailsPage vm={vm} />,
  })
})

const UserDetailsPage = observer(function UserDetailsPage({
  vm,
}: UserDetailsPageProps) {
  return (
    <UserDetails
      isSidebarOpen={vm.membershipsTabVm.isSidebarOpen}
      header={
        <UserDetailsHeader
          avatar={vm.user.avatar}
          displayName={vm.user.displayName}
          userDataType={vm.user.userDataType}
          email={vm.user.email}
          canCreateJob={vm.canCreateJobForUser}
          canEditUserInformation={vm.canEditUserInformation}
          onAddToTeam={vm.addUserToTeamVm.modal.show}
          onAddToWorkspace={vm.addUserToWorkspaceVm.modal.show}
          onCreateJob={vm.createJob}
          onEditUserInformation={vm.editUserInformation}
        />
      }
      tabs={
        <UserDetailsTabs
          tabs={vm.tabs}
          currentTab={vm.currentTab}
          onTabChange={vm.setTab}
        />
      }
      information={<UserDetailsInformationContainer vm={vm} />}
      tabContent={match(vm.currentTab, {
        Memberships: () => <MembershipsTabContainer vm={vm.membershipsTabVm} />,
        Jobs: () => <UserJobsTabContainer vm={vm.jobsTabVm} />,
        Documents: () => <DocumentsTabContainer vm={vm.documentsTabVm} />,
        Clients: () => <AdvisorClientsTabContainer vm={vm.clientsTabVm} />,
      })}
      dialogs={
        <>
          <AddUserToTeamDialogContainer vm={vm.addUserToTeamVm} />
          <AddUserToWorkspaceDialogContainer vm={vm.addUserToWorkspaceVm} />
          <AddCustomerToWorkspaceDialogContainer
            vm={vm.addCustomerToWorkspaceVm}
          />
          <JoinWorkspaceAsAdminDialogContainer vm={vm.joinWorkspaceAsAdminVm} />
          <EditUserInformationDialogContainer vm={vm.editUserInformationVm} />
          <EditProDetailsDialogContainer vm={vm.editProDetailsVm} />
          <EditUserRoleDialogContainer vm={vm.editUserRoleVm} />
          <RemoveFromWorkspaceDialogContainer vm={vm.removeFromWorkspaceVm} />
          <ManageUserCapacityDialogContainer vm={vm.manageUserCapacityVm} />
          <UserInfoItemListDialogContainer vm={vm.userInfoItemListVm} />
          <ManageSkillsListDialogContainer vm={vm.manageSkillListVm} />
          <RestoreToWorkspaceDialogContainer vm={vm.restoreToWorkspaceVm} />
          <PromoteToOwnerDialogContainer vm={vm.promoteToOwnerVm} />
        </>
      }
      sidebar={<MembershipsSidebarContainer vm={vm.membershipsTabVm} />}
    />
  )
})
